import {useChangeVehicleTypeMutation} from '@dms/api/sales';
import {VehicleTypeEnum} from '@dms/api/vehicle';
import {handleApiError} from '@dms/shared';

import {ApiError, Nullish} from 'shared/web';

type UpdateVehicleTypeResult =
  | {success: true; isTypeChanged: true; isTypeSkipped: false}
  | {
      success: true;
      isTypeChanged: false;
      isTypeSkipped: true;
      reason: 'NO_FORM_TYPE' | 'SAME_TYPE' | 'LOADING_VEHICLE' | 'VEHICLE_ERROR';
    }
  | {success: false; isTypeChanged: false; isTypeSkipped: false; error: ApiError};

// oldType is only compared against the submitted type. GetVehicleApiResponse types it as a loose
// string, so narrowing it here would only force callers into a cast.
export const useUpdateVehicleType = (vehicleId: string, oldType: string | Nullish) => {
  const [updateType, changeTypeMutationMetadata] = useChangeVehicleTypeMutation();

  const updateVehicleType = (newType: VehicleTypeEnum): Promise<UpdateVehicleTypeResult> => {
    if (!vehicleId || !newType) {
      return Promise.resolve({
        success: true,
        isTypeChanged: false,
        isTypeSkipped: true,
        reason: 'LOADING_VEHICLE',
      });
    }

    if (newType === oldType) {
      return Promise.resolve({
        success: true,
        isTypeChanged: false,
        isTypeSkipped: true,
        reason: 'SAME_TYPE',
      });
    }

    return updateType({vehicleId, type: newType})
      .unwrap()
      .then(() => ({success: true, isTypeChanged: true, isTypeSkipped: false}) as const)
      .catch(
        (error: ApiError) =>
          ({
            success: false,
            isTypeChanged: false,
            isTypeSkipped: false,
            error,
          }) as const
      );
  };

  const updateVehicleTypeWithErrorHandling = async (
    newType: VehicleTypeEnum,
    options?: {silent?: boolean}
  ) => {
    const result = await updateVehicleType(newType);

    if (!result.success && !options?.silent) {
      handleApiError(result.error);
    }

    return result;
  };

  return [
    updateVehicleTypeWithErrorHandling,
    {
      isLoading: changeTypeMutationMetadata.isLoading,
      isError: changeTypeMutationMetadata.isError,
      rawUpdateFunction: updateVehicleType,
    },
  ] as const;
};
